import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { isAzureADEnabled } from '../../config/authConfig';
import AzureLoginButton from '../../components/AzureLoginButton';
import { clearMsalSessionStorage } from '../../utils/msalSessionStorage';

type AuthMethod = 'local' | 'microsoft';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>('local');

  // Clear any stale MSAL cache when Login page mounts
  // This handles the case where user was auto-logged-out (session timeout)
  // and needs to login with a different account (local or Azure AD)
  useEffect(() => {
    // Clear MSAL-related sessionStorage entries to prevent interference with login
    // This is safe because if user is on login page, they should be able to start fresh
    clearMsalSessionStorage();
  }, []);

  const { login, loginWithAzureAD } = useAuth();
  const location = useLocation();
  const message = (location.state as any)?.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      const errorMessage = err.message || 'Invalid credentials. Please try again.';
      setError(errorMessage);

      // If user needs Microsoft auth, switch to Microsoft tab automatically
      if (isAzureADEnabled && errorMessage.includes('Microsoft authentication')) {
        setAuthMethod('microsoft');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAzureLoginSuccess = async (accessToken: string, id_token?: string) => {
    setError('');
    setLoading(true);

    try {
      await loginWithAzureAD(accessToken, id_token);
    } catch (err: any) {
      setError(err.message || 'Azure AD authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAzureLoginError = (error: any) => {
    console.error('Azure login error:', error);
    setError('Microsoft sign-in failed. Please try again.');
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Sign in to your account</h2>

      {message && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">{message}</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Auth Method Selector */}
      {isAzureADEnabled && (
        <div className="mb-6">
          <div className="flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setAuthMethod('local')}
              className={`flex-1 px-6 py-2 text-sm font-medium rounded-l-lg border ${
                authMethod === 'local'
                  ? 'bg-blue-50 text-blue-700 border-blue-200 z-10'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              } focus:z-10 focus:ring-2 focus:ring-blue-500 transition-colors duration-200`}
            >
              Local Account
            </button>
            <button
              type="button"
              onClick={() => setAuthMethod('microsoft')}
              className={`flex-1 px-6 py-2 text-sm font-medium rounded-r-lg border-t border-r border-b ${
                authMethod === 'microsoft'
                  ? 'bg-blue-50 text-blue-700 border-blue-200 z-10'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              } focus:z-10 focus:ring-2 focus:ring-blue-500 transition-colors duration-200`}
            >
              Microsoft Account
            </button>
          </div>
        </div>
      )}


      {/* Microsoft Authentication */}
      {authMethod === 'microsoft' && isAzureADEnabled && (
        <div className="space-y-6">
          <AzureLoginButton
            onLoginSuccess={handleAzureLoginSuccess}
            onLoginError={handleAzureLoginError}
          />
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or</span>
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => setAuthMethod('local')}
            className="w-full text-center text-sm text-blue-600 hover:text-blue-500"
          >
            Use local account instead
          </button>
        </div>
      )}

      {/* Local Authentication Form */}
      {authMethod === 'local' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          {isAzureADEnabled && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => setAuthMethod('microsoft')}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-500"
              >
                Use Microsoft account instead
              </button>
            </>
          )}
        </form>
      )}

    </div>
  );
};

export default Login; 